import React, { useState, useEffect, useMemo } from 'react';
import { Switch, Button, Tag, Spin, Popconfirm, message, Tooltip, Collapse, Tabs, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import { getUserPermissions, updateUserPermission, resetUserPermissions } from '../../api/users';

/**
 * Yetkiler paneli.
 *
 * Eskiden burada ELLE yazılmış bir `PERMISSION_GROUPS` dizisi vardı ve 87 ham
 * izin anahtarını tek tek gösteriyordu. İki sorun doğuruyordu:
 *
 * 1. Bir ekran birden fazla prefix'ten izin kullanıyor (Öğrenci Detayı tek
 *    başına 8 prefix'e dokunuyor). Şube müdürü "bu kişi öğrencilere erişsin"
 *    derken 8 yeri tek tek işaretlemek zorunda kalıyordu.
 * 2. Elle tutulan liste yeni modülde güncellenmeyi İKİ KEZ unuttu; izin
 *    backend'de tanımlı ama panelde görünmediği için kimseye VERİLEMİYORDU.
 *
 * Artık katalog backend'den geliyor (`PermissionCatalog`) ve orada bir test
 * her iznin en az bir pakette geçtiğini garanti ediyor. Bu dosyada anahtar
 * listesi TUTULMAZ — yeni modül eklerken burayı güncellemen gerekmiyor.
 */

// Rol varsayılanı / elle verildi / elle kaldırıldı renkleri (eski panelden
// korundu, kullanıcı bu ayrıma alışkın).
const RENK_VARSAYILAN = '#52c41a';
const RENK_ELLE_VER   = '#1677ff';
const RENK_ELLE_KALDIR = '#ff4d4f';

const UserPermissionsPanel = ({ user }) => {
  const { user: caller } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user) fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const res = await getUserPermissions(user.id);
      setData(res.data);
    } catch {
      message.error('İzinler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const prefix = data?.package_prefix || 'paket:';
  const effective = useMemo(() => new Set(data?.effective || []), [data]);
  const roleDefaults = useMemo(() => new Set(data?.role_defaults || []), [data]);

  const overrideOf = (key) =>
    data?.overrides?.find(o => o.permission_key === key) || null;

  /**
   * Bir paket aksiyonunun durumu. Üç hal var — "kısmen" hali gerçek:
   * rol varsayılanı paketin bazı izinlerini içerip bazılarını içermeyebilir
   * (ör. şube müdürü ödemede delete hariç her şeye sahip).
   */
  const aksiyonDurumu = (grants) => {
    const acik = grants.filter(g => effective.has(g)).length;
    if (acik === 0) return 'kapali';
    if (acik === grants.length) return 'acik';
    return 'kismen';
  };

  const varsayilanDurumu = (grants) => {
    const acik = grants.filter(g => roleDefaults.has(g)).length;
    if (acik === 0) return 'kapali';
    if (acik === grants.length) return 'acik';
    return 'kismen';
  };

  const applyToggle = async (key, checked) => {
    setSaving(key);
    try {
      await updateUserPermission(user.id, { permission_key: key, is_granted: checked });
      // Etkin liste sunucuda hesaplanıyor (paket açılımı orada yapılıyor),
      // yerelde tahmin etmek yerine yeniden çekiyoruz. Aksi halde "kısmen"
      // durumları yanlış gösterilirdi.
      await fetchPermissions();
      // İzin listesi kullanıcının tarayıcısında (localStorage) tutuluyor ve
      // yalnızca uygulama açılışında /auth/me ile tazeleniyor. Kişi o an açık
      // oturumdaysa değişiklik menüsüne HEMEN yansımaz — 2026-08-07'de
      // "yetkiyi verdim ama menüde çıkmadı" diye geri bildirim geldi.
      message.success(
        checked
          ? 'Yetki verildi. Kişi açık oturumdaysa çıkış yapıp tekrar girmeli.'
          : 'Yetki kaldırıldı. Kişi açık oturumdaysa çıkış yapıp tekrar girmeli.'
      );
    } catch (e) {
      message.error(e?.response?.data?.message || 'İzin güncellenemedi.');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async () => {
    try {
      await resetUserPermissions(user.id);
      message.success('İzinler rol varsayılanına sıfırlandı.');
      fetchPermissions();
    } catch {
      message.error('Sıfırlama başarısız.');
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><Spin /></div>;
  }

  const catalog = data.catalog || {};
  const hasOverrides = data.overrides?.length > 0;

  const renkAcikla = (
    <div className="flex gap-4 text-sm flex-wrap">
      {[
        [RENK_VARSAYILAN, 'Rol varsayılanı'],
        [RENK_ELLE_VER, 'Elle verildi'],
        [RENK_ELLE_KALDIR, 'Elle kaldırıldı'],
      ].map(([renk, etiket]) => (
        <span key={etiket} className="flex items-center gap-1">
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: renk }} />
          {etiket}
        </span>
      ))}
    </div>
  );

  // --- Paket görünümü (herkes) -------------------------------------------
  const paketGorunumu = (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Bir yetki verdiğinizde o ekranın çalışması için gereken tüm izinler birlikte açılır."
        description="Hangi izinleri kapsadığını görmek için paketin altındaki “Kapsam” satırını açın."
      />

      {Object.entries(catalog).map(([paketKey, paket]) => (
        <div key={paketKey} className="mb-5">
          <div className="font-semibold text-gray-700">{paket.label}</div>
          <div className="text-xs text-gray-500 mb-2">{paket.description}</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(paket.actions).map(([aksiyonKey, aksiyon]) => {
              const key = `${prefix}${paketKey}.${aksiyonKey}`;
              const durum = aksiyonDurumu(aksiyon.grants);
              const varsayilan = varsayilanDurumu(aksiyon.grants);
              const override = overrideOf(key);

              let kenar = '#f0f0f0';
              if (override) kenar = override.is_granted ? RENK_ELLE_VER : RENK_ELLE_KALDIR;
              else if (durum === 'kismen') kenar = '#faad14';

              let anahtarRengi;
              if (durum === 'acik') {
                anahtarRengi = override?.is_granted ? RENK_ELLE_VER : RENK_VARSAYILAN;
              } else if (override && !override.is_granted) {
                anahtarRengi = RENK_ELLE_KALDIR;
              }

              return (
                <div
                  key={key}
                  className="flex items-center justify-between p-2 rounded border"
                  style={{ borderColor: kenar }}
                >
                  <div className="min-w-0 pr-2">
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      {aksiyon.label}
                      {override && (
                        <Tooltip title={`Rol varsayılanı: ${varsayilan === 'acik' ? 'Aktif' : varsayilan === 'kismen' ? 'Kısmen' : 'Pasif'}`}>
                          <Tag color={override.is_granted ? 'blue' : 'red'} style={{ fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>
                            {override.is_granted ? 'Elle verildi' : 'Elle kaldırıldı'}
                          </Tag>
                        </Tooltip>
                      )}
                      {!override && durum === 'kismen' && (
                        <Tooltip title="Bu paketin izinlerinin bir kısmı açık. Anahtarı açarsanız tamamı verilir.">
                          <Tag color="orange" style={{ fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>
                            Kısmen
                          </Tag>
                        </Tooltip>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{aksiyon.hint}</div>
                  </div>
                  <Switch
                    size="small"
                    checked={durum === 'acik'}
                    loading={saving === key}
                    style={anahtarRengi ? { backgroundColor: anahtarRengi } : undefined}
                    onChange={(checked) => applyToggle(key, checked)}
                  />
                </div>
              );
            })}
          </div>

          <Collapse
            ghost
            size="small"
            items={[{
              key: 'kapsam',
              label: <span className="text-xs text-gray-500">Kapsam — hangi izinleri açıyor?</span>,
              children: (
                <div className="text-xs">
                  {Object.entries(paket.actions).map(([aksiyonKey, aksiyon]) => (
                    <div key={aksiyonKey} className="mb-1">
                      <span className="text-gray-600">{aksiyon.label}:</span>{' '}
                      {aksiyon.grants.map(g => (
                        <Tag key={g} style={{ fontSize: 11, marginInlineEnd: 4 }}>{g}</Tag>
                      ))}
                    </div>
                  ))}
                </div>
              ),
            }]}
          />
        </div>
      ))}
    </div>
  );

  // --- Gelişmiş görünüm (yalnızca developer_admin) -------------------------
  // Kaçış yolu: paketlerin karşılamadığı istisnai bir durumda tek tek izin
  // verilebilsin. Anahtar listesi yine ELLE tutulmuyor - katalogdaki
  // grants'lardan türetiliyor, yani yeni izin buraya da otomatik düşüyor.
  const gelismisGorunum = () => {
    const tumIzinler = new Set();
    Object.values(catalog).forEach(p =>
      Object.values(p.actions).forEach(a => a.grants.forEach(g => tumIzinler.add(g)))
    );

    const prefixeGore = {};
    [...tumIzinler].sort().forEach(k => {
      const p = k.includes('.') ? k.split('.')[0] : 'diğer';
      (prefixeGore[p] = prefixeGore[p] || []).push(k);
    });

    return (
      <div>
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Tek tek izin görünümü"
          description="Normalde paketleri kullanın. Burada verilen tekil izinler paket anahtarlarıyla birlikte çalışır; son yapılan işlem geçerlidir."
        />
        {Object.entries(prefixeGore).map(([p, keys]) => (
          <div key={p} className="mb-4">
            <div className="font-semibold text-gray-600 mb-2">{p}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {keys.map(k => {
                const override = overrideOf(k);
                const acik = effective.has(k);
                return (
                  <div
                    key={k}
                    className="flex items-center justify-between p-2 rounded border"
                    style={{ borderColor: override ? (override.is_granted ? RENK_ELLE_VER : RENK_ELLE_KALDIR) : '#f0f0f0' }}
                  >
                    <span className="text-xs font-mono">{k}</span>
                    <Switch
                      size="small"
                      checked={acik}
                      loading={saving === k}
                      style={{ backgroundColor: acik ? (override?.is_granted ? RENK_ELLE_VER : RENK_VARSAYILAN) : (override && !override.is_granted ? RENK_ELLE_KALDIR : undefined) }}
                      onChange={(checked) => applyToggle(k, checked)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const sekmeler = [{ key: 'paketler', label: 'Yetkiler', children: paketGorunumu }];
  if (caller?.role === 'developer_admin') {
    sekmeler.push({ key: 'gelismis', label: 'Gelişmiş', children: gelismisGorunum() });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        {renkAcikla}
        {hasOverrides && (
          <Popconfirm
            title="Tüm özel izinler silinip rol varsayılanına dönülsün mü?"
            onConfirm={handleReset}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button size="small" icon={<ReloadOutlined />} danger>
              Varsayılana Sıfırla
            </Button>
          </Popconfirm>
        )}
      </div>

      <Tabs items={sekmeler} />
    </div>
  );
};

export default UserPermissionsPanel;
